import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, Plus, Printer, RefreshCw, Trash2 } from "lucide-react";
import PageHeader, { card, ghostBtn } from "@/app/PageHeader";
import { useSession } from "@/app/session";

// Net profit per location per quarter, after cost of goods, fees, the loan payment share and other costs,
// and the commission each location is owed. Backend: backend/profit_share.py at /api/profit-share/*.

const field = "h-10 w-full rounded-md border border-white/10 bg-vortex-ink/60 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-vortex-cyan";
const label = "font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase";
const btnPrimary = "flex items-center justify-center gap-2 rounded-full bg-vortex-cyan px-5 py-2.5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(92,179,226,0.4)] disabled:opacity-50";

const money = (n) => (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const today = () => new Date().toISOString().slice(0, 10);
const CAT_LABELS = {
  insurance: "Insurance", repairs: "Repairs", supplies: "Supplies", car_parking: "Parking & tolls",
  car_actual: "Vehicle costs", utilities: "Utilities & phone", rent_property: "Storage rent", rent_equipment: "Equipment rent",
  office: "Office & software", taxes_licenses: "Taxes & licenses", legal: "Legal & accounting", advertising: "Advertising",
  contract_labor: "Contract labor", wages: "Wages", other: "Other", mileage: "Mileage not tied to a location",
};

const currentQuarter = () => {
  const d = new Date();
  return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
};

const Line = ({ title, value, minus, strong, sub }) => (
  <tr className="border-b border-white/5">
    <td className={`py-1.5 pr-3 ${strong ? "font-medium text-white" : "text-slate-300"}`}>
      {title}{sub && <span className="block text-xs text-slate-500">{sub}</span>}
    </td>
    <td className={`py-1.5 text-right tabular-nums ${strong ? "font-semibold text-white" : minus ? "text-slate-400" : "text-slate-200"}`}>
      {minus && value ? `- ${money(value)}` : money(value)}
    </td>
  </tr>
);

const esc = (t) => String(t ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const statementHtml = (r, rep) => {
  const row = (t, v, minus) => `<tr><td>${t}</td><td style="text-align:right">${minus && v ? "- " : ""}${money(v)}</td></tr>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(r.name)} Q${rep.quarter} ${rep.year}</title>
<style>body{font-family:system-ui,sans-serif;max-width:640px;margin:40px auto;color:#111}td{padding:4px 0;border-bottom:1px solid #eee}
table{width:100%;border-collapse:collapse}h1{font-size:20px}small{color:#666}.t td{font-weight:700;border-top:2px solid #111}</style></head><body>
<h1>Vortex Vending Services: commission statement</h1>
<p><b>${esc(r.name)}</b><br>${esc(r.address)}<br>Q${rep.quarter} ${rep.year} (${rep.from} to ${rep.to})<br>
<small>${r.machines} machine${r.machines === 1 ? "" : "s"}${r.live_since ? `, live since ${r.live_since}` : ""}. ${r.orders} sales, ${r.units} items.</small></p>
<table>${row("Gross sales", r.gross_sales + r.revenue_adjustments)}${row(`Sales tax collected (${r.tax_rate}%)`, r.sales_tax, true)}
${row("Net sales", r.net_sales)}${row("Cost of products sold", r.cogs, true)}${row("Card processing fees", r.card_fees, true)}
${r.machine_fees ? row("Machine service fees", r.machine_fees, true) : ""}${row("Equipment financing (machine share)", r.loan, true)}
${r.expenses ? row("Repairs and costs at this location", r.expenses, true) : ""}${r.mileage ? row("Service trips (IRS mileage rate)", r.mileage, true) : ""}
${r.shared_costs ? row("Share of general operating costs", r.shared_costs, true) : ""}${r.other_costs ? row("Other costs", r.other_costs, true) : ""}
<tr class="t"><td>Net profit</td><td style="text-align:right">${money(r.net_profit)}</td></tr>
${r.carried_loss_in ? row("Loss carried from earlier quarters", r.carried_loss_in, true) : ""}
<tr class="t"><td>Commission (${r.commission_pct ?? 0}% of ${r.commission_basis === "gross" ? "net sales" : "net profit"})</td><td style="text-align:right">${money(r.commission)}</td></tr></table>
${r.payout ? `<p>Paid ${r.payout.paid_on}${r.payout.check_no ? `, check #${esc(r.payout.check_no)}` : ""}: ${money(r.payout.amount)}</p>` : ""}
<p><small>Net profit is sales after sales tax, less the cost of the products sold, payment processing fees, this location's share of the equipment financing, and costs for servicing this location. No commission is due for a quarter with a loss.</small></p>
<script>window.print()</script></body></html>`;
};

const LocationDetail = ({ r, rep, onChanged }) => {
  const { api, onAuthError } = useSession();
  const [adj, setAdj] = useState({ kind: "cost", amount: "", note: "" });
  const [pay, setPay] = useState({ amount: "", paid_on: today(), check_no: "" });
  useEffect(() => { setPay({ amount: r.payout?.amount ?? r.commission, paid_on: r.payout?.paid_on || today(), check_no: r.payout?.check_no || "" }); }, [r]);

  const addAdj = async (e) => {
    e.preventDefault();
    try {
      await api.post("/profit-share/adjustments", { location_id: r.location_id, year: rep.year, quarter: rep.quarter,
        kind: adj.kind, amount: Number(adj.amount), note: adj.note });
      setAdj({ kind: "cost", amount: "", note: "" });
      onChanged();
    } catch (err) { onAuthError(err, "Couldn't add that line. Check the amount."); }
  };
  const delAdj = async (id) => {
    try { await api.delete(`/profit-share/adjustments/${id}`); onChanged(); } catch (err) { onAuthError(err, "Couldn't remove it."); }
  };
  const savePay = async () => {
    try {
      await api.put("/profit-share/payouts", { location_id: r.location_id, year: rep.year, quarter: rep.quarter,
        amount: Number(pay.amount) || 0, paid_on: pay.paid_on, check_no: pay.check_no });
      toast.success("Marked paid.");
      onChanged();
    } catch (err) { onAuthError(err, "Couldn't save the payment."); }
  };
  const undoPay = async () => {
    try {
      await api.delete("/profit-share/payouts", { params: { location_id: r.location_id, year: rep.year, quarter: rep.quarter } });
      onChanged();
    } catch (err) { onAuthError(err, "Couldn't undo."); }
  };
  const print = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print the statement."); return; }
    w.document.write(statementHtml(r, rep));
    w.document.close();
  };

  const loanSub = r.loan_lines.filter((l) => l.amount).map((l) => `${l.month}: ${money(l.payment)} / financed machines x ${l.machines}${l.live_share < 1 ? ` x ${Math.round(l.live_share * 100)}% of month live` : ""}`).join("; ");
  return (
    <div className="grid grid-cols-1 gap-6 border-t border-white/10 bg-vortex-ink/30 p-4 lg:grid-cols-2" data-testid={`detail-${r.location_id}`}>
      <div>
        <table className="w-full text-sm">
          <tbody>
            <Line title="Gross sales" value={r.gross_sales} sub={`${r.orders} orders, ${r.units} items from Sandstar`} />
            {r.revenue_adjustments > 0 && <Line title="Other revenue" value={r.revenue_adjustments} />}
            <Line title={`Sales tax inside prices (${r.tax_rate}%)`} value={r.sales_tax} minus />
            <Line title="Net sales" value={r.net_sales} strong />
            <Line title="Cost of goods" value={r.cogs} minus sub={r.missing_cost_units ? `${r.missing_cost_units} items have no cost set` : null} />
            <Line title="Card fees" value={r.card_fees} minus />
            {r.machine_fees > 0 && <Line title="Machine fees" value={r.machine_fees} minus />}
            <Line title="Loan payment share" value={r.loan} minus sub={loanSub || (r.live_since ? null : "Not live yet: no sales saved")} />
            <Line title="Location expenses" value={r.expenses} minus sub={r.expense_lines.map((e) => `${e.date} ${e.vendor || e.category} ${money(e.amount)}`).join("; ") || null} />
            <Line title="Mileage" value={r.mileage} minus sub={r.mileage_lines.map((t) => `${t.date} ${money(t.amount)}`).join("; ") || null} />
            <Line title="Shared costs" value={r.shared_costs} minus sub={r.shared_weight ? `${Math.round(r.shared_weight * 1000) / 10}% of shared costs` : null} />
            <Line title="Other costs" value={r.other_costs} minus />
            <Line title="Net profit" value={r.net_profit} strong />
            {r.carried_loss_in > 0 && <Line title="Loss carried in" value={r.carried_loss_in} minus />}
            <Line title={`Commission (${r.commission_pct ?? "?"}% of ${r.commission_basis === "gross" ? "net sales" : "net profit"})`} value={r.commission} strong />
          </tbody>
        </table>
      </div>
      <div className="space-y-5">
        <div>
          <p className={label}>Extra lines this quarter</p>
          <ul className="mt-2 space-y-1 text-sm">
            {[...r.revenue_lines, ...r.cost_lines].map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 text-slate-300">
                <span>{a.kind === "revenue" ? "+" : "-"} {money(a.amount)} <span className="text-slate-500">{a.note}</span></span>
                <button className="rounded p-1 text-slate-500 hover:text-red-300" onClick={() => delAdj(a.id)} aria-label="Remove line"><Trash2 size={13} /></button>
              </li>
            ))}
          </ul>
          <form onSubmit={addAdj} className="mt-2 flex flex-wrap gap-2">
            <select aria-label="Kind" className={field.replace("w-full", "w-32")} value={adj.kind} onChange={(e) => setAdj({ ...adj, kind: e.target.value })}>
              <option value="cost">Cost</option><option value="revenue">Revenue</option>
            </select>
            <input aria-label="Amount" type="number" step="0.01" min="0.01" required className={field.replace("w-full", "w-28")} placeholder="0.00"
              value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} />
            <input aria-label="Note" className={field.replace("w-full", "min-w-0 flex-1")} placeholder="What it was (cash sales, refund, spoilage)"
              value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} />
            <button className={ghostBtn} type="submit"><Plus size={12} /> Add</button>
          </form>
        </div>
        <div>
          <p className={label}>Payment to the location</p>
          {r.payout ? (
            <p className="mt-2 text-sm text-emerald-300">Paid {money(r.payout.amount)} on {r.payout.paid_on}{r.payout.check_no && `, check #${r.payout.check_no}`}.
              <button className="ml-2 text-xs text-slate-400 hover:underline" onClick={undoPay}>Undo</button></p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              <input aria-label="Amount paid" type="number" step="0.01" min="0" className={field.replace("w-full", "w-28")} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
              <input aria-label="Paid on" type="date" className={field.replace("w-full", "w-40")} value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} />
              <input aria-label="Check number" className={field.replace("w-full", "w-28")} placeholder="Check #" value={pay.check_no} onChange={(e) => setPay({ ...pay, check_no: e.target.value })} />
              <button className={ghostBtn} onClick={savePay}>Mark paid</button>
            </div>
          )}
        </div>
        <button className={ghostBtn} onClick={print}><Printer size={12} /> Print statement</button>
      </div>
    </div>
  );
};

const SettingsPanel = ({ data, onSaved }) => {
  const { api, onAuthError } = useSession();
  const [s, setS] = useState(data.settings);
  const [months, setMonths] = useState(Object.entries(data.settings.loan_months || {}).map(([m, a]) => ({ m, a })));
  useEffect(() => { setS(data.settings); setMonths(Object.entries(data.settings.loan_months || {}).map(([m, a]) => ({ m, a }))); }, [data]);
  const set = (patch) => setS((x) => ({ ...x, ...patch }));
  const num = (k) => (e) => set({ [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...s, loan_machines: Number(s.loan_machines), loan_payment: Number(s.loan_payment), card_fee_pct: Number(s.card_fee_pct),
        card_fee_per_order: Number(s.card_fee_per_order), monthly_fee_per_machine: Number(s.monthly_fee_per_machine),
        loan_months: Object.fromEntries(months.filter((x) => x.m).map((x) => [x.m, Number(x.a) || 0])) };
      delete body.id; delete body.updated_at;
      await api.put("/profit-share/settings", body);
      toast.success("Saved.");
      onSaved();
    } catch (err) { onAuthError(err, "Couldn't save. Check the numbers and dates."); }
  };
  const toggleCat = (c) => set({ shared_categories: s.shared_categories.includes(c) ? s.shared_categories.filter((x) => x !== c) : [...s.shared_categories, c] });

  return (
    <form onSubmit={save} className="space-y-6" data-testid="profit-settings">
      <div className={`${card} grid grid-cols-1 gap-4 p-5 sm:grid-cols-3`}>
        <p className={`${label} sm:col-span-3`}>Equipment loan (North Mill)</p>
        <label className="space-y-1.5 text-sm text-slate-300">Monthly payment<input type="number" step="0.01" min="0" className={field} value={s.loan_payment} onChange={num("loan_payment")} /></label>
        <label className="space-y-1.5 text-sm text-slate-300">Machines it pays for<input type="number" min="1" className={field} value={s.loan_machines} onChange={num("loan_machines")} /></label>
        <label className="space-y-1.5 text-sm text-slate-300">First payment month<input type="month" className={field} value={s.loan_start} onChange={(e) => set({ loan_start: e.target.value })} /></label>
        <div className="sm:col-span-3">
          <p className="text-sm text-slate-300">Months with a different payment</p>
          {months.map((x, i) => (
            <div key={i} className="mt-2 flex gap-2">
              <input type="month" aria-label="Month" className={field.replace("w-full", "w-44")} value={x.m} onChange={(e) => setMonths(months.map((y, j) => (j === i ? { ...y, m: e.target.value } : y)))} />
              <input type="number" step="0.01" min="0" aria-label="Payment" className={field.replace("w-full", "w-32")} value={x.a} onChange={(e) => setMonths(months.map((y, j) => (j === i ? { ...y, a: e.target.value } : y)))} />
              <button type="button" className="rounded p-2 text-slate-500 hover:text-red-300" onClick={() => setMonths(months.filter((_, j) => j !== i))} aria-label="Remove month"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" className={`${ghostBtn} mt-2`} onClick={() => setMonths([...months, { m: "", a: "" }])}><Plus size={12} /> Month</button>
          <p className="mt-2 text-xs text-slate-500">Each month's payment is split evenly over the financed machines. A location carries one share per machine, from the day it went live.</p>
        </div>
      </div>

      <div className={`${card} grid grid-cols-1 gap-4 p-5 sm:grid-cols-3`}>
        <p className={`${label} sm:col-span-3`}>Fees</p>
        <label className="space-y-1.5 text-sm text-slate-300">Card fee (% of sales)<input type="number" step="0.01" min="0" className={field} value={s.card_fee_pct} onChange={num("card_fee_pct")} /></label>
        <label className="space-y-1.5 text-sm text-slate-300">Card fee per sale ($)<input type="number" step="0.01" min="0" className={field} value={s.card_fee_per_order} onChange={num("card_fee_per_order")} /></label>
        <label className="space-y-1.5 text-sm text-slate-300">Monthly fee per machine ($)<input type="number" step="0.01" min="0" className={field} value={s.monthly_fee_per_machine} onChange={num("monthly_fee_per_machine")} /></label>
      </div>

      <div className={`${card} space-y-3 p-5`}>
        <p className={label}>Shared costs (not tied to a location)</p>
        <select aria-label="Shared cost split" className={field.replace("w-full", "w-full sm:w-96")} value={s.shared_mode} onChange={(e) => set({ shared_mode: e.target.value })}>
          <option value="per_machine">Split by machines live</option>
          <option value="by_sales">Split by each location's sales</option>
          <option value="none">Don't deduct shared costs</option>
        </select>
        {s.shared_mode !== "none" && (
          <div className="flex flex-wrap gap-2">
            {data.shared_choices.map((c) => (
              <label key={c} className={`cursor-pointer rounded-full border px-3 py-1 text-xs ${s.shared_categories.includes(c) ? "border-vortex-cyan text-vortex-cyan" : "border-white/15 text-slate-400"}`}>
                <input type="checkbox" className="sr-only" checked={s.shared_categories.includes(c)} onChange={() => toggleCat(c)} />{CAT_LABELS[c] || c}
              </label>
            ))}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={s.carry_losses} onChange={(e) => set({ carry_losses: e.target.checked })} />
          Carry a location's losing quarter forward against its next profits
        </label>
      </div>

      <div className={`${card} p-5`}>
        <p className={label}>Per location</p>
        <table className="mt-3 w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Location</th><th>Sales tax %</th><th>Live since</th></tr></thead>
          <tbody>
            {data.locations.map((l) => (
              <tr key={l.id} className="border-t border-white/5">
                <td className="py-2 pr-2 text-slate-200">{l.name}</td>
                <td className="py-2 pr-2"><input type="number" step="0.01" min="0" aria-label={`${l.name} sales tax`} className={field.replace("w-full", "w-24")}
                  placeholder={String(l.default_tax_rate)} value={s.tax_rates[l.id] ?? ""}
                  onChange={(e) => { const t = { ...s.tax_rates }; if (e.target.value === "") delete t[l.id]; else t[l.id] = Number(e.target.value); set({ tax_rates: t }); }} /></td>
                <td className="py-2"><input type="date" aria-label={`${l.name} live since`} className={field.replace("w-full", "w-40")} value={s.live_since[l.id] || ""}
                  onChange={(e) => { const t = { ...s.live_since }; if (!e.target.value) delete t[l.id]; else t[l.id] = e.target.value; set({ live_since: t }); }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-slate-500">Blank sales tax uses 6% (8% in Philadelphia). Blank live date uses the first sale Sandstar shows there.</p>
      </div>
      <button className={btnPrimary} type="submit">Save settings</button>
    </form>
  );
};

const ProfitShare = () => {
  const { api, onAuthError } = useSession();
  const [{ year, quarter }, setQ] = useState(currentQuarter);
  const [tab, setTab] = useState("report");
  const [rep, setRep] = useState(null);
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(null);
  const [pulling, setPulling] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([api.get("/profit-share/report", { params: { year, quarter } }), api.get("/profit-share/settings")]);
      setRep(r.data); setSettings(s.data);
    } catch (err) { onAuthError(err, "Couldn't load the profit share report."); }
  }, [api, onAuthError, year, quarter]);
  useEffect(() => { load(); }, [load]);

  const pull = async () => {
    setPulling(true);
    try {
      const res = await api.post("/planner/sandstar/machines/sync", null, { params: { days: 120 } });
      const saved = (res.data.synced || []).reduce((n, m) => n + (m.orders_saved || 0), 0);
      toast.success(`Pulled ${saved} sales from Sandstar.`);
      load();
    } catch (err) { onAuthError(err, err.response?.data?.detail || "Couldn't reach Sandstar."); }
    finally { setPulling(false); }
  };
  const download = async () => {
    try {
      const res = await api.get("/profit-share/export.csv", { params: { year, quarter }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `profit-share-${year}-Q${quarter}.csv` });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { onAuthError(err, "Couldn't build the download."); }
  };

  const quarters = [];
  for (let y = currentQuarter().year; y >= 2026; y -= 1) for (let q = 4; q >= 1; q -= 1) if (y > 2026 || q >= 3) quarters.push(`${y}-${q}`);

  return (
    <div>
      <PageHeader title="Location Profit Share"
        intro="Net profit for each location each quarter, after cost of goods, fees, its share of the machine loan and other costs, and the commission its agreement says it gets."
        actions={(
          <>
            <select aria-label="Quarter" className={field.replace("w-full", "w-36")} value={`${year}-${quarter}`}
              onChange={(e) => { const [y, q] = e.target.value.split("-").map(Number); setQ({ year: y, quarter: q }); setOpen(null); }}>
              {quarters.map((k) => { const [y, q] = k.split("-"); return <option key={k} value={k}>Q{q} {y}</option>; })}
            </select>
            <button className={ghostBtn} onClick={pull} disabled={pulling}><RefreshCw size={12} className={pulling ? "animate-spin" : ""} /> {pulling ? "Pulling..." : "Pull sales"}</button>
            <button className={ghostBtn} onClick={download}><Download size={12} /> CSV</button>
          </>
        )} />

      <div className="mb-6 flex gap-1 border-b border-white/10" role="tablist">
        {[["report", "Report"], ["settings", "Settings"]].map(([k, t]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${tab === k ? "border-vortex-cyan text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{t}</button>
        ))}
      </div>

      {tab === "settings" && settings && <SettingsPanel data={settings} onSaved={load} />}

      {tab === "report" && rep && (
        <div className="space-y-6" data-testid="profit-report">
          {rep.warnings.length > 0 && (
            <ul className="space-y-1 rounded-md border border-amber-400/30 bg-amber-400/5 p-4 text-sm text-amber-200">
              {rep.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[["Gross sales", rep.totals.gross_sales], ["Total costs", rep.totals.total_costs], ["Net profit", rep.totals.net_profit],
              ["Commissions owed", rep.totals.commission]].map(([t, v]) => (
              <div key={t} className={`${card} p-4`}><p className={label}>{t}</p><p className="mt-2 text-2xl font-semibold text-white">{money(v)}</p></div>
            ))}
          </div>
          {rep.locations.length === 0 ? (
            <div className={`${card} p-10 text-center text-sm text-slate-500`}>No locations with machines yet. Add machines under Accounts &amp; Service.</div>
          ) : (
            <div className={`${card} overflow-x-auto`}>
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-xs text-slate-500">
                    <th className="px-4 py-2">Location</th><th className="px-2 text-right">Net sales</th><th className="px-2 text-right">Costs</th>
                    <th className="px-2 text-right">Net profit</th><th className="px-2 text-right">Rate</th><th className="px-2 text-right">Owed</th><th className="px-4">Paid</th>
                  </tr>
                </thead>
                {rep.locations.map((r) => (
                  <tbody key={r.location_id}>
                    <tr className="cursor-pointer border-b border-white/5 hover:bg-white/5" onClick={() => setOpen(open === r.location_id ? null : r.location_id)}>
                      <td className="px-4 py-3 text-slate-100">
                        <span className="flex items-center gap-2">{open === r.location_id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{r.name}</span>
                        <span className="ml-6 block text-xs text-slate-500">{r.machines} machine{r.machines === 1 ? "" : "s"}{r.live_since ? ` · live ${r.live_since}` : " · no sales yet"}</span>
                      </td>
                      <td className="px-2 text-right tabular-nums text-slate-200">{money(r.net_sales)}</td>
                      <td className="px-2 text-right tabular-nums text-slate-400">{money(r.total_costs)}</td>
                      <td className={`px-2 text-right tabular-nums ${r.net_profit < 0 ? "text-red-300" : "text-white"}`}>{money(r.net_profit)}</td>
                      <td className="px-2 text-right text-slate-400">{r.commission_pct == null ? "?" : `${r.commission_pct}%`}</td>
                      <td className="px-2 text-right font-semibold tabular-nums text-vortex-cyan">{money(r.commission)}</td>
                      <td className="px-4 text-xs">{r.payout ? <span className="text-emerald-300">{r.payout.paid_on}</span> : r.commission > 0 ? <span className="text-amber-300">Not yet</span> : <span className="text-slate-500">Nothing due</span>}</td>
                    </tr>
                    {open === r.location_id && (
                      <tr><td colSpan={7} className="p-0"><LocationDetail r={r} rep={rep} onChanged={load} /></td></tr>
                    )}
                  </tbody>
                ))}
              </table>
            </div>
          )}
          {rep.shared.total > 0 && (
            <p className="text-xs text-slate-500">Shared costs this quarter: {money(rep.shared.total)} ({rep.shared.lines.length} items), split {rep.shared.mode === "by_sales" ? "by sales" : "by machines live"}.</p>
          )}
          <p className="text-xs text-slate-500">
            The agreements say "10% of net profit" without defining it. Net profit here is sales after sales tax, less cost of goods, card fees, the
            location's share of the loan payment, and costs logged against it. Commissions are paid quarterly by check; a quarter with a loss pays nothing.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfitShare;
